/**
 * Time Transformer - Date and time conversion utilities
 *
 * Handles conversion of relative time expressions to absolute timestamps
 * for use in graph queries.
 *
 * @module queryBuilderCore/transformers/timeTransformer
 */

/**
 * Converts relative time expressions to ISO timestamp strings
 * @param value - Relative time string (e.g., "3 days", "2 hours", "1 week")
 * @returns ISO timestamp string or null if invalid
 * @example
 * convertRelativeTimeToTimestamp("3 days") // "2025-10-15T00:00:00.000Z"
 * convertRelativeTimeToTimestamp("invalid") // null
 */
export function convertRelativeTimeToTimestamp(value: string): string | null {
  if (!value || typeof value !== 'string') return null;

  const trimmedValue = value.trim().toLowerCase();

  const match = trimmedValue.match(
    /^(\d+)\s*(day|days|hour|hours?|week|weeks?|month|months?)$/
  );
  if (!match) return null;

  const amount = parseInt(match[1], 10);
  const unit = match[2];

  if (isNaN(amount) || amount <= 0) return null;

  const now = Math.floor(Date.now() / (60 * 1000)) * (60 * 1000);
  let millisecondsAgo: number;

  if (unit.startsWith('day')) {
    millisecondsAgo = amount * 24 * 60 * 60 * 1000;
  } else if (unit.startsWith('hour')) {
    millisecondsAgo = amount * 60 * 60 * 1000;
  } else if (unit.startsWith('week')) {
    millisecondsAgo = amount * 7 * 24 * 60 * 60 * 1000;
  } else if (unit.startsWith('month')) {
    millisecondsAgo = amount * 30 * 24 * 60 * 60 * 1000;
  } else {
    return null;
  }

  return new Date(now - millisecondsAgo).toISOString();
}
